import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, AwardIcon, BookOpenIcon, GraduationCapIcon, BuildingIcon, PhoneIcon, MailIcon, ArrowRightIcon } from 'lucide-react';
import { universities } from '../data/universities';
import { usePageMeta } from '../hooks/usePageMeta';
const brandColors: Record<string, {
  gradient: string;
  accent: string;
  tag: string;
}> = {
  'global-tech-college': {
    gradient: 'bg-gradient-to-br from-[#1B365D] to-[#8B1A1A]',
    accent: 'text-[#D4A843]',
    tag: 'bg-[#1B365D]/10 text-[#1B365D]'
  },
  'ultimate-medical-academy': {
    gradient: 'bg-gradient-to-br from-[#003B5C] to-[#176B9C]',
    accent: 'text-[#00BFA5]',
    tag: 'bg-[#003B5C]/10 text-[#003B5C]'
  },
  'university-of-arizona-global-campus': {
    gradient: 'bg-gradient-to-br from-[#1C2B39] to-[#003366]',
    accent: 'text-[#CC0033]',
    tag: 'bg-[#1C2B39]/10 text-[#1C2B39]'
  },
  'colorado-christian-university': {
    gradient: 'bg-gradient-to-br from-[#0F3057] to-[#1A4A7A]',
    accent: 'text-[#D4A943]',
    tag: 'bg-[#0F3057]/10 text-[#0F3057]'
  },
  'houston-christian-university': {
    gradient: 'bg-gradient-to-br from-[#002F6C] to-[#001A3D]',
    accent: 'text-[#A6192E]',
    tag: 'bg-[#002F6C]/10 text-[#002F6C]'
  }
};
const defaultBrand = {
  gradient: 'bg-navy',
  accent: 'text-gold',
  tag: 'bg-navy/5 text-navy'
};
export function CollegesPage() {
  usePageMeta('Browse Online Colleges & Universities | OnlineCollegeGuide', 'Explore accredited online colleges and universities. Compare programs, read reviews, and request free information from top schools.', {
    ogImage: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  return <main className="flex-grow bg-light" data-id="element-1137" data-mp="2qn9yfemkjhi5">
      {/* Hero */}
      <div className="hero-bg text-white py-20" data-id="element-1138" data-mp="2mmg7sdhqrolk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1139" data-mp="2mnrasb3d4sbm">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-gold text-sm font-semibold mb-6" data-id="element-1140" data-mp="2tqforfxpho0z">
            <BuildingIcon className="w-4 h-4 mr-2" data-id="element-1141" data-mp="2ymmx1fdkrbm3" />
            Partner Schools
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 text-white" data-id="element-1142" data-mp="33mj27hy0d938">
            Get More Information About
            <br className="hidden sm:block" data-id="element-1143" data-mp="3cgnorp63zycy" /> Our Partner Colleges
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed" data-id="element-1144" data-mp="3hf8ntl9mkbzt">
            Explore accredited colleges and universities offering online
            programs. Select a school to learn more and request information.
          </p>
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-1145" data-mp="1s0alfwfzomw3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-1146" data-mp="1s258bgatmp2t">
          {universities.map(uni => {
          const brand = brandColors[uni.slug] || defaultBrand;
          return <Link key={uni.slug} to={`/colleges/${uni.slug}`} className="group bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-xl hover:border-gray-300 transition-all duration-300 flex flex-col" data-id="element-1147" data-mp="e63vn6w1ixiw">
                {/* Card Header with School Brand Colors */}
                <div className={`${brand.gradient} p-6 lg:p-7 flex items-center gap-4 relative overflow-hidden`} data-id="element-1148" data-mp="x23n8gl3vz1y">
                  {uni.logoUrl ? <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center flex-shrink-0 overflow-hidden p-2 shadow-lg" data-id="element-1149" data-mp="o1np7mn4tk4h">
                      <img src={uni.logoUrl} alt={`${uni.name} logo`} className="w-full h-full object-contain" data-id="element-1150" data-mp="j5gfrjvj1qhs" />
                    </div> : <div className="w-14 h-14 rounded-xl bg-white/15 border-2 border-white/25 flex items-center justify-center flex-shrink-0" data-id="element-1151" data-mp="vju0dez9uxx9">
                      <GraduationCapIcon className="w-7 h-7 text-white" data-id="element-1152" data-mp="10dpv88k8o4o7" />
                    </div>}
                  <div className="relative z-10 min-w-0" data-id="element-1153" data-mp="rjb67e9wpfrw">
                    <h2 className="text-lg font-bold text-white leading-tight" data-id="element-1154" data-mp="rjt8arj92o7q">
                      {uni.name}
                    </h2>
                    <div className="flex items-center text-white/70 text-sm mt-1.5" data-id="element-1155" data-mp="1jah8a096tnjt">
                      <MapPinIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1156" data-mp="1j84v00edk1d3" />
                      {uni.location}
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6 flex flex-col flex-grow" data-id="element-1157" data-mp="3q0x7beplkh92">
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3" data-id="element-1158" data-mp="3pzcdxsf0r4cs">
                    {uni.description}
                  </p>

                  {/* Accreditation */}
                  <div className="flex items-start text-xs text-gray-500 mb-4 bg-gray-50 rounded-lg p-3" data-id="element-1159" data-mp="3nf66vbv3yxs0">
                    <AwardIcon className="w-4 h-4 mr-2 text-gold flex-shrink-0 mt-0.5" data-id="element-1160" data-mp="3igwbp19dvx6e" />
                    <span className="leading-relaxed" data-id="element-1161" data-mp="ai2ffm28bec">{uni.accreditation}</span>
                  </div>

                  {/* Contact Info. Stacked rather than side by side: at a third
                      of the row an email truncates to uselessness in a column. */}
                  <div className="space-y-2 mb-4" data-id="element-1162" data-mp="2zrga0alypyoe">
                    <div className="flex items-center text-xs text-gray-500" data-id="element-1163" data-mp="2zsrdsafer6fi">
                      <PhoneIcon className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" data-id="element-1164" data-mp="3dj388fzgth9t" />
                      <span data-id="element-1165" data-mp="38qtaids3zcwz">{uni.phone}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500 min-w-0" data-id="element-1166" data-mp="2l1ih5hd38ksv">
                      <MailIcon className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" data-id="element-1167" data-mp="2g37838umaebp" />
                      <span className="truncate" data-id="element-1168" data-mp="jzsr26qdjswi">{uni.email}</span>
                    </div>
                  </div>

                  {/* Programs Preview */}
                  <div className="pt-4 border-t border-gray-100 mt-auto" data-id="element-1169" data-mp="oxb160g4oe63">
                    <div className="flex items-center text-xs font-bold text-gray-800 uppercase tracking-wider mb-3" data-id="element-1170" data-mp="tpcd1l9dw2vl">
                      <BookOpenIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1171" data-mp="yndvc3yzjy60" />
                      {uni.programs.length} Programs Available
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1172" data-mp="5zrngjiq67dc">
                      {uni.programs.slice(0, 3).map(prog => <span key={prog.slug} className={`text-xs ${brand.tag} px-2.5 py-1.5 rounded-lg font-medium`} data-id="element-1173" data-mp="ay1ld1vu3m2n">
                          {prog.shortTitle}
                        </span>)}
                      {uni.programs.length > 3 && <span className="text-xs text-gray-400 px-2 py-1.5" data-id="element-1174" data-mp="1qhpmi56wb7tn">
                          +{uni.programs.length - 3} more
                        </span>}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3" data-id="element-1175" data-mp="3240wm7tde8ff">
                    <span className="text-sm font-bold text-navy group-hover:text-action transition-colors duration-200" data-id="element-1176" data-mp="2x68vcn6u59yk">
                      View Programs
                    </span>
                    <div className="w-9 h-9 rounded-full bg-navy/5 flex items-center justify-center flex-shrink-0 group-hover:bg-navy transition-colors duration-200" data-id="element-1177" data-mp="2n6rqg7zer03n">
                      <ArrowRightIcon className="w-4 h-4 text-navy group-hover:text-white transition-colors duration-200" data-id="element-1178" data-mp="2e8ep2gx3dcjv" />
                    </div>
                  </div>
                </div>
              </Link>;
        })}
        </div>

        {/* Empty State */}
        {universities.length === 0 && <div className="text-center py-16" data-id="element-1179" data-mp="3hhms5smwsgqd">
            <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-1180" data-mp="3mfeulgl6jl8t" />
            <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1181" data-mp="3meln2deecrpm">
              No colleges listed yet
            </h3>
            <p className="text-gray-500" data-id="element-1182" data-mp="37oo0u3012c32">
              Check back soon — we're adding new partner schools.
            </p>
          </div>}
      </div>
    </main>;
}